import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Snackbar, IconButton, Alert, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ControlledDataGrid from '../../../components/DataGrid/ControlledDataGrid';
import { CSVDownloader } from "react-papaparse";
import axios from "axios";
import { APIURL } from "../../../config";

const GRID_ID = 44;
const REPORT_NAME = "EVV Invoice";

// csv_header is the label used for the CSV/print exports; headerName is the on-screen label.
const columns = [
  {
    field: "id",
    headerName: "INVOICE ID",
    width: 200,
    hide: true,
    csv_header: "Id",
  },
  {
    field: "invoice_date",
    headerName: "DATE OF INVOICE",
    width: 200,
    csv_header: "Date Of Invoice",
  },
  {
    field: "cus_name",
    headerName: "CUSTOMER NAME",
    width: 200,
    csv_header: "Cutomer Name",
  },
  {
    field: "procProcCode",
    headerName: "PROCEDURE CODE",
    align: "center",
    width: 200,
    csv_header: "Procedure Name",
  },
  {
    field: "invoice_number",
    headerName: "INVOICE NUMBER",
    width: 200,
    csv_header: "Invoice Number",
  },
  {
    field: "line_item_no",
    headerName: "LINE ITEM NUMBER",
    width: 200,
    csv_header: "Line Item Number",
  },
  {
    field: "amount",
    headerName: "AMOUNT",
    width: 200,
    csv_header: "Amount",
  },
];

const CELL_STYLE = "width:100%; border-bottom: 1px solid black";

const escapeHtml = (value) =>
  String(value).replace(/[&<>]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[char]));

// Opens a print window containing the exported rows (arrays ordered to match `columns`).
const printRows = (exportRows) => {
  const printWindow = window.open("");
  if (!printWindow) return;

  const header = columns
    .map((col) => `<th style='${CELL_STYLE}'>${escapeHtml(col.headerName)}</th>`)
    .join("");
  const body = exportRows
    .map((row) => `<tr>${row.map((cell) => `<td style='${CELL_STYLE}'>${escapeHtml(cell)}</td>`).join("")}</tr>`)
    .join("");

  printWindow.document.write(`<table><tr>${header}</tr>${body}</table>`);
  printWindow.print();
  printWindow.close();
};

const StatusSnackbar = ({ severity, message, onClose }) => (
  <Snackbar open={message !== ""} autoHideDuration={30000} onClose={onClose}>
    <Alert sx={{ width: "100%" }} severity={severity} elevation={6} variant="filled">
      {message}
      <IconButton aria-label="close" color="inherit" sx={{ p: 0.25 }} onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </Alert>
  </Snackbar>
);

const EvvInvoice = React.forwardRef((props, ref) => {
  const { user, comp_id } = props;
  const apiRef = useGridApiRef();
  useImperativeHandle(ref, () => apiRef.current);

  const [invoiceReports, setInvoiceReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportDetails, setReportDetails] = useState({});
  const [isFavorite, setIsFavorite] = useState(false);
  const [successText, setSuccessText] = useState("");
  const [errorText, setErrorText] = useState("");

  const fetchData = useCallback(() => {
    axios
      .get(APIURL + "/jsreport/getAllReports", { params: { empID: user.emp_id } })
      .then((response) => {
        const report = response?.data?.reports.find((ele) => ele.report_name === REPORT_NAME);
        setReportDetails(report ?? {});
        setIsFavorite(report?.status === 1);
      })
      .catch((err) => console.log(err));

    axios
      .get(APIURL + "/invoice", { params: { comp_id, emp_id: user.emp_id } })
      .then((response) => {
        const invoices = [...(response?.data ?? [])].sort((a, b) =>
          a.invoice_number > b.invoice_number ? 1 : b.invoice_number > a.invoice_number ? -1 : 0
        );
        setInvoiceReports(invoices);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setErrorText("Unable to load invoices");
        setLoading(false);
      });
  }, [user.emp_id, comp_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const markFavourite = () => {
    const payload = {
      report_id: reportDetails?.report_id,
      status: isFavorite ? 0 : 1,
      empID: user.emp_id,
    };

    axios
      .put(APIURL + "/jsreport/markFavorite", payload)
      .then((response) => {
        if (response?.status === 200 && response?.data?.code === 200) {
          setSuccessText(response?.data?.status_message || "Favorite Modifed Succesfully");
        } else {
          setErrorText("Something went wrong");
        }

        fetchData();
      })
      .catch((err) => {
        console.log(err);
        setErrorText("Something went wrong");
      });
  };

  const rows = useMemo(
    () =>
      invoiceReports.map((invoice) =>
        Object.fromEntries(columns.map((col) => [col.field, invoice[col.field]]))
      ),
    [invoiceReports]
  );

  // Positional rows shared by the CSV download and the print view.
  const exportRows = useMemo(
    () => invoiceReports.map((invoice) => columns.map((col) => invoice[col.field] ?? "-")),
    [invoiceReports]
  );

  // CSVDownloader serializes this on click, so no CSV is built until the user asks for one.
  const csvData = useMemo(
    () => ({ fields: columns.map((col) => col.csv_header), data: exportRows }),
    [exportRows]
  );

  return (
    <div style={{ margin: 0, width: '100%', padding: 10, boxSizing: 'border-box' }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}><div />
          <div>
            <CSVDownloader data={csvData} filename={"EVV report"} bom={true}>
              <button type="button" className="btn btn-sm btn-outline-info m-1">
                CSV
              </button>
            </CSVDownloader>

            <button
              type="button"
              className="btn btn-sm btn-outline-info m-1"
              onClick={() => printRows(exportRows)}
            >
              PDF
            </button>
            <Tooltip title={isFavorite ? "Remove from favorite reports" : "Add to favorite reports"}>
              <img
                src={isFavorite ? "./star-solid.svg" : "./star-regular.svg"}
                alt="barchart"
                style={{
                  fontFamily: "Lato",
                  width: "1.5rem",
                  height: "1.5rem",
                  cursor: "pointer",
                  margin: 5
                }}
                onClick={markFavourite}
              />
            </Tooltip>
          </div>
        </div>
        <div style={{ height: 'calc(100vh - 250px)' }}>
          <ControlledDataGrid
            gridId={GRID_ID}
            user={user}
            initialState={{ sorting: { sortModel: [{ field: "invoice_date", sort: "desc" }] } }}
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            checkboxSelection={true}
            loading={loading}
          />
        </div>
      </div>
      <StatusSnackbar severity="success" message={successText} onClose={() => setSuccessText("")} />
      <StatusSnackbar severity="error" message={errorText} onClose={() => setErrorText("")} />
    </div>
  );
});

export default EvvInvoice;